import "@/App.css";
import "@/design.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nProvider } from "@/lib/i18n";
import { MySkusProvider } from "@/lib/mySkus";
import api from "@/lib/api";
import { ReleaseProvider, useRelease } from "@/contexts/ReleaseContext";
import { ReleaseBoundary } from "@/components/ReleaseBoundary";
import ReleaseStatusPage from "@/pages/ReleaseStatusPage";
import Sidebar from "@/components/Sidebar";
import LoginPage from "@/pages/LoginPage";
import NotificationBell from "@/components/NotificationBell";
import { Menu } from "lucide-react";

// Perf sprint Feb 2026 — route-based code splitting. Each page is fetched on
// demand the first time it's navigated to. Cuts initial JS bundle from ~600KB
// to ~80KB (only the login page + sidebar shell load up-front).
const MyProductsPage = lazy(() => import("@/pages/MyProductsPage"));
// 15 Oct 2026 — information architecture: every figure appears once.
// Overview (home) · My Products · Pricing (overpriced, cheapest, comparison,
// price wars, discounts) · Market · Opportunities · Competitors. The old
// Price & Market Intel, Scanner and Discounts addresses redirect into Pricing.
const OverviewPage = lazy(() => import("@/pages/OverviewPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const OpportunitiesPage = lazy(() => import("@/pages/OpportunitiesPage"));
const StoreRegistryPage = lazy(() => import("@/pages/StoreRegistryPage"));
const DataQualityPage = lazy(() => import("@/pages/DataQualityPage"));
const KeyBrandsPage = lazy(() => import("@/pages/KeyBrandsPage"));
const SalesPricesPage = lazy(() => import("@/pages/SalesPricesPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const AlertsPage = lazy(() => import("@/pages/AlertsPage"));
const CompetitorProfilePage = lazy(() => import("@/pages/CompetitorProfilePage"));
// iter79 — Market Share tab (measured shares, gaps, brands, categories)
const MarketSharePage = lazy(() => import("@/pages/MarketSharePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ImportPage = lazy(() => import("@/pages/ImportPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
// iter73v (Aug 8 2026) — admin-only coverage / recrawl / rematch console.
const CoverageReportPage = lazy(() => import("@/pages/CoverageReportPage"));

// Perf sprint Feb 2026 — frontend-side stale-while-revalidate cache.
// 60s staleTime matches backend's TTL cache so refetches are correctly aligned.
// Tab switches under 60s are instant; older data revalidates in background.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,           // data is fresh for 60s
      gcTime: 5 * 60 * 1000,          // keep in cache for 5min
      refetchOnWindowFocus: false,    // don't refetch every time user tabs back
      // Axios owns the single analytics-timeout retry; don't multiply it here.
      retry: (attempt, error) => error?.code !== "ERR_CANCELED" && !error?.config?._readRetryAttempt && attempt < 1,
    },
  },
});

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// canHavePageAccess: super_admin sees all; everyone else must have the page in allowed_pages.
export function canAccessPage(user, pageKey) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  const pages = Array.isArray(user.allowed_pages) ? user.allowed_pages : [];
  return pages.includes(pageKey);
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-loader">
      <div className="w-8 h-8 rounded-full border-2 border-[#1E988E] border-t-transparent animate-spin" />
    </div>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("daleel_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("daleel_token"); })
      .finally(() => setChecking(false));
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("daleel_token", token);
    localStorage.setItem("daleel_user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("daleel_token");
    localStorage.removeItem("daleel_user");
    setUser(null);
    queryClient.clear();
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("daleel_user", JSON.stringify(data));
      return data;
    } catch (_e) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, checking, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, pageKey, pageKeys }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="flex items-center justify-center min-h-screen bg-[#090E1C]"><div className="w-8 h-8 rounded-full border-2 border-[#1E988E] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (pageKey && !canAccessPage(user, pageKey)) return <Navigate to="/no-access" replace />;
  if (pageKeys && !pageKeys.some((k) => canAccessPage(user, k))) return <Navigate to="/no-access" replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Where each page lives, in menu order. A user without the Overview lands on
// the first page they may open instead of "no access" (the client's
// tables-only account, 2 Nov 2026).
const PAGE_HOME = [["overview", "/"], ["sales_prices", "/sales-prices"], ["my_products", "/products"],
  ["pricing", "/pricing"], ["market_share", "/market-share"], ["key_brands", "/key-brands"],
  ["opportunities", "/opportunities"], ["competitors", "/stores"], ["reports", "/reports"],
  ["data_quality", "/data-quality"], ["alerts", "/alerts"], ["import", "/import"], ["settings", "/settings"]];

function HomeRoute() {
  const { user } = useAuth();
  if (user && !canAccessPage(user, "overview")) {
    const home = PAGE_HOME.find(([k]) => canAccessPage(user, k));
    return <Navigate to={home ? home[1] : "/no-access"} replace />;
  }
  return <ProtectedRoute pageKey="overview"><OverviewPage /></ProtectedRoute>;
}

function SuperAdminRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="flex items-center justify-center min-h-screen bg-[#090E1C]"><div className="w-8 h-8 rounded-full border-2 border-[#1E988E] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/no-access" replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function NoAccessPage() {
  const { user, logout } = useAuth();
  return (
    <div className="p-10 text-center" data-testid="no-access-page">
      <h1 className="text-2xl mb-3" style={{ color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.05em", textTransform: "uppercase" }}>
        Access Restricted
      </h1>
      <p className="text-sm mb-2" style={{ color: "#A1E4DB" }}>
        Your account doesn't have permission to view this page.
      </p>
      {user?.email && (
        <p className="text-xs mb-6" style={{ color: "#A1E4DB", opacity: 0.7 }}>
          Signed in as <span style={{ color: "#6AC1B5" }}>{user.email}</span>. Contact your super admin to request access.
        </p>
      )}
      <button onClick={logout} className="hrm-btn-primary px-6 py-2 text-xs" data-testid="no-access-logout-btn">
        Sign Out
      </button>
    </div>
  );
}

function AppLayout() {
  const { user, checking } = useAuth();
  const release = useRelease();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  useEffect(() => {
    const close = (event) => { if (event.key === "Escape") setMobileNavOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);
  if (checking) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="flex min-h-screen" style={{ background: "radial-gradient(circle at top center, #090E1C 0%, #090E1C 100%)" }} data-testid="app-layout">
      {mobileNavOpen && <button className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" data-testid="mobile-nav-backdrop" />}
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} onCollapse={setNavCollapsed} />
      <main className={`app-main flex-1 min-w-0 min-h-screen flex flex-col ms-0 ${navCollapsed ? "md:ms-[68px]" : "md:ms-[232px]"}`}>
        <div className="flex items-center justify-between md:justify-end px-4 md:px-6 py-3 border-b border-white/5">
          <button className="md:hidden p-2 text-[#A1E4DB]" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation" aria-expanded={mobileNavOpen} data-testid="mobile-nav-open"><Menu className="w-5 h-5" /></button>
          {!release.price_comparison_only && canAccessPage(user, "alerts") && <NotificationBell />}
        </div>
        <div className="flex-1 min-w-0">
        <ReleaseBoundary><Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/sales-prices" element={<ProtectedRoute pageKey="sales_prices"><SalesPricesPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute pageKey="my_products"><MyProductsPage /></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute pageKey="pricing"><PricingPage /></ProtectedRoute>} />
          <Route path="/opportunities" element={<ProtectedRoute pageKey="opportunities"><OpportunitiesPage /></ProtectedRoute>} />
          <Route path="/insights" element={<Navigate to="/pricing" replace />} />
          <Route path="/price-intel" element={<Navigate to="/pricing" replace />} />
          <Route path="/scanner" element={<Navigate to="/pricing?tab=overpriced" replace />} />
          <Route path="/discounts" element={<Navigate to="/pricing?tab=discounts" replace />} />
          <Route path="/alerts" element={<ProtectedRoute pageKey="alerts"><AlertsPage /></ProtectedRoute>} />
          <Route path="/market-share" element={<ProtectedRoute pageKey="market_share"><MarketSharePage /></ProtectedRoute>} />
          <Route path="/stores" element={<ProtectedRoute pageKey="competitors"><StoreRegistryPage /></ProtectedRoute>} />
          <Route path="/data-quality" element={<ProtectedRoute pageKey="data_quality"><DataQualityPage /></ProtectedRoute>} />
          <Route path="/key-brands" element={<ProtectedRoute pageKey="key_brands"><KeyBrandsPage /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute pageKey="reports"><ReportsPage /></ProtectedRoute>} />
          <Route path="/stores/:storeId" element={<ProtectedRoute pageKey="competitors"><CompetitorProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute pageKey="settings"><ReleaseSettings /></ProtectedRoute>} />
          <Route path="/settings/coverage" element={<ProtectedRoute pageKey="settings"><CoverageReportPage /></ProtectedRoute>} />
          <Route path="/import" element={<ProtectedRoute pageKey="import"><ImportPage /></ProtectedRoute>} />
          <Route path="/users" element={<SuperAdminRoute><UsersPage /></SuperAdminRoute>} />
          <Route path="/no-access" element={<ProtectedRoute><NoAccessPage /></ProtectedRoute>} />
        </Routes></ReleaseBoundary>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <ReleaseProvider>
          <MySkusProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/*" element={<AppLayout />} />
              </Routes>
              <Toaster position="top-right" />
            </BrowserRouter>
          </MySkusProvider>
          </ReleaseProvider>
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;

function ReleaseSettings() {
  return <ReleaseStatusPage />;
}
